import { yupResolver } from '@hookform/resolvers/yup';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ObjectSchema, boolean, number, object, string } from 'yup';

import { disableAutofillBg } from 'src/common/components/constants/disableAutofillBg';
import {
  ControlledSelectField,
  SelectOption,
} from 'src/common/components/form/controlled/ControlledSelectField';
import LocalizedTextField from 'src/common/components/form/LocalizedTextField';
import EditorFormActions from 'src/common/components/modals/EditorFormActions';
import MediaManagerModal from 'src/common/components/modals/MediaManagerModal';
import { LanguageKeys } from 'src/common/constants/languages';
import { emptyLocalizedString, normalizeLocalizedString } from 'src/helpers/localizedStringUtils';
import { parseGPXFile } from 'src/helpers/parseGPXFile';
import { IBaseTrackMap, ITrackData, ITrackMap } from 'src/interfaces/TrackMap';
import { UploadedFile } from 'src/interfaces/FileManager';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import useDeferredFileUsage from 'src/hooks/useDeferredFileUsage';

type FormTrackMap = IBaseTrackMap & {
  i18n: Record<LanguageKeys, string>;
};

interface TrackMapEditorFormProps {
  handleClose?: () => void;
  handleSave: (
    editedTrackMap: ITrackData & IBaseTrackMap,
    id?: string,
    onSuccess?: () => Promise<void>,
  ) => Promise<void>;
  selectedTrackMap?: ITrackMap;
  isSaving?: boolean;
  onBusyChange?: (busy: boolean) => void;
}

const TrackMapEditorForm = (props: TrackMapEditorFormProps) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const { handleSave, handleClose, selectedTrackMap, isSaving, onBusyChange } = props;

  const [isProcessingGpx, setIsProcessingGpx] = useState(false);
  const isBusy = Boolean(isSaving || isProcessingGpx);

  const { distances } = useSystemOptionsStore(systemOptionsState => systemOptionsState);
  const { enqueueSnackbar } = useSnackbar();
  const { addPendingFile, markPendingFilesAsUsed, clearPendingFiles } = useDeferredFileUsage({
    contentType: 'trackMap',
    contentId: selectedTrackMap?.id || 'new',
    fieldName: 'gpxFile',
  });

  const [editorLang, setEditorLang] = useState<LanguageKeys>(LanguageKeys.en);
  const [isMediaManagerOpen, setIsMediaManagerOpen] = useState(false);
  const [trackData, setTrackData] = useState<ITrackData>({
    bounds: selectedTrackMap?.bounds || {
      northEast: [0, 0],
      southWest: [0, 0],
    },
    checkPoints: selectedTrackMap?.checkPoints || [],
    trackPositions: selectedTrackMap?.trackPositions || [],
    dataLabels: selectedTrackMap?.dataLabels || [],
    elevations: selectedTrackMap?.elevations || [],
    levelDiff: selectedTrackMap?.levelDiff || {
      pos: 0,
      neg: 0,
    },
    totalDistance: selectedTrackMap?.totalDistance || 0,
    pointsOfInterest: selectedTrackMap?.pointsOfInterest || [],
  });

  useEffect(() => {
    return () => {
      clearPendingFiles();
    };
  }, [clearPendingFiles]);

  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  const trackMapSchema: ObjectSchema<FormTrackMap> = object()
    .shape({
      distance: string().required(t('form.yup.genericRequired')),
      fileName: string().required(t('form.yup.genericRequired')),
      fileUrl: string(),
      name: string()
        .trim()
        .matches(/^[a-z0-9-]+$/, t('form.yup.lettersNumbersDashesOnly'))
        .required(t('form.yup.genericRequired')),
      i18n: object()
        .shape({
          [LanguageKeys.en]: string().required(t('form.yup.genericRequired')),
          [LanguageKeys.ro]: string().required(t('form.yup.genericRequired')),
          [LanguageKeys.hu]: string().required(t('form.yup.genericRequired')),
        })
        .required(t('form.yup.genericRequired')),
      route: object()
        .shape({
          i18n: object()
            .shape({
              [LanguageKeys.en]: string().required(t('form.yup.genericRequired')),
              [LanguageKeys.ro]: string().required(t('form.yup.genericRequired')),
              [LanguageKeys.hu]: string().required(t('form.yup.genericRequired')),
            })
            .required(t('form.yup.genericRequired')),
        })
        .required(t('form.yup.genericRequired')),
      time: number().required(t('form.yup.genericRequired')),
      published: boolean(),
    })
    .required();

  const defaultValues: Partial<FormTrackMap> = selectedTrackMap
    ? {
        distance: selectedTrackMap.distance,
        name: selectedTrackMap.name,
        i18n: normalizeLocalizedString(selectedTrackMap.i18n),
        fileName: selectedTrackMap.fileName,
        fileUrl: selectedTrackMap.fileUrl,
        route: selectedTrackMap.route,
        time: selectedTrackMap.time,
        published: selectedTrackMap.published ?? true,
      }
    : {
        published: true,
        i18n: emptyLocalizedString(),
        route: { i18n: emptyLocalizedString() },
      };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<FormTrackMap>({
    resolver: yupResolver(trackMapSchema),
    defaultValues,
  });

  const handleTrackMapSelect = async (file: UploadedFile) => {
    if (file.uploadEndpoint !== 'trackMap') {
      return;
    }

    setIsMediaManagerOpen(false);
    setIsProcessingGpx(true);
    try {
      const response = await fetch(file.fileUrl);
      const gpxContent = await response.text();

      if (gpxContent) {
        const parsedTrackData = parseGPXFile(gpxContent);
        setValue('fileName', file.fileName);
        setValue('fileUrl', file.fileUrl);
        setTrackData(parsedTrackData);
        addPendingFile(file.id);

        enqueueSnackbar(t('system.trackMaps.fileSelectSuccess'), {
          variant: 'success',
        });
      }
    } catch (error) {
      console.error('Error processing selected file:', error);
      const message =
        error instanceof Error ? error.message : t('system.trackMaps.errors.fileSelectError');
      enqueueSnackbar(message, {
        variant: 'error',
      });
    } finally {
      setIsProcessingGpx(false);
    }
  };

  const onSubmit: SubmitHandler<FormTrackMap> = async formData => {
    if (isBusy) {
      return;
    }
    if (!trackData.trackPositions.length) {
      enqueueSnackbar(t('system.trackMaps.errors.invalidGPXStructure'), {
        variant: 'error',
      });
      return;
    }
    const editedTrackMap: ITrackData & IBaseTrackMap = {
      ...trackData,
      ...formData,
    };

    await handleSave(editedTrackMap, selectedTrackMap?.id, markPendingFilesAsUsed);
  };

  const distanceOptions: SelectOption[] = [];

  distances.forEach(dist =>
    distanceOptions.push({
      label: dist.i18n[currentLang],
      value: dist.id,
    }),
  );

  const i18nFieldError = errors.i18n?.[editorLang]?.message;
  const routeI18nFieldError = errors.route?.i18n?.[editorLang]?.message;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 4 }}>
            {watch('fileName') && watch('fileUrl') && (
              <Box
                sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}
              >
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  {t('system.trackMaps.selectedFile')}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {watch('fileName')}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  disabled={isBusy}
                  onClick={() => {
                    setValue('fileName', '');
                    setValue('fileUrl', '');
                    setTrackData({
                      bounds: { northEast: [0, 0], southWest: [0, 0] },
                      checkPoints: [],
                      trackPositions: [],
                      dataLabels: [],
                      elevations: [],
                      levelDiff: { pos: 0, neg: 0 },
                      totalDistance: 0,
                      pointsOfInterest: [],
                    });
                  }}
                  sx={{ mt: 1 }}
                >
                  {t('base.remove')}
                </Button>
              </Box>
            )}

            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              disabled={isBusy}
              onClick={() => setIsMediaManagerOpen(true)}
              sx={{ width: '100%' }}
            >
              {watch('fileName') && watch('fileUrl')
                ? t('system.trackMaps.replaceGPXFile')
                : t('system.trackMaps.selectGPXFile')}
            </Button>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField
              sx={disableAutofillBg}
              label={t('system.trackMaps.routeLengthLabel')}
              variant="outlined"
              type="number"
              fullWidth
              margin="dense"
              disabled={isBusy}
              value={trackData.totalDistance}
              onChange={event => {
                const next = Number(event.target.value);
                setTrackData(prev => ({
                  ...prev,
                  totalDistance: Number.isFinite(next) ? next : 0,
                }));
              }}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">km</InputAdornment>,
                },
              }}
            />
            <TextField
              sx={disableAutofillBg}
              label={t('system.trackMaps.levelAscentLabel')}
              variant="outlined"
              type="number"
              fullWidth
              margin="dense"
              disabled={isBusy}
              value={trackData.levelDiff.pos}
              onChange={event => {
                const next = Number(event.target.value);
                setTrackData(prev => ({
                  ...prev,
                  levelDiff: {
                    ...prev.levelDiff,
                    pos: Number.isFinite(next) ? next : 0,
                  },
                }));
              }}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                },
              }}
            />
            <TextField
              sx={disableAutofillBg}
              label={t('system.trackMaps.levelDescentLabel')}
              variant="outlined"
              type="number"
              fullWidth
              margin="dense"
              disabled={isBusy}
              value={trackData.levelDiff.neg}
              onChange={event => {
                const next = Number(event.target.value);
                setTrackData(prev => ({
                  ...prev,
                  levelDiff: {
                    ...prev.levelDiff,
                    neg: Number.isFinite(next) ? next : 0,
                  },
                }));
              }}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            {!!trackData && (
              <>
                <Typography variant="body2" color={grey[600]}>
                  {t('system.trackMaps.trackPointsNrLabel')}:
                </Typography>
                <Typography variant="body2">{trackData.trackPositions.length}</Typography>
              </>
            )}

            <Typography variant="body2" color={grey[600]}>
              {t('system.trackMaps.fileName')}:
            </Typography>
            <Typography variant="body2">{watch('fileName') || '-'}</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              sx={disableAutofillBg}
              label={t('technicalTypes.nameLabel')}
              variant="outlined"
              fullWidth
              margin="dense"
              error={!!errors['name']}
              helperText={errors['name']?.message}
              {...register('name')}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              sx={disableAutofillBg}
              label={t('system.trackMaps.timeLabel')}
              variant="outlined"
              type="number"
              fullWidth
              margin="dense"
              error={!!errors['time']}
              helperText={errors['time']?.message}
              {...register('time')}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t('system.trackMaps.timeUOM')}</InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <LocalizedTextField
              label={t('technicalTypes.translatedNameLabel')}
              value={watch('i18n')}
              onChange={value =>
                setValue('i18n', value, { shouldValidate: true, shouldDirty: true })
              }
              editorLang={editorLang}
              onEditorLangChange={setEditorLang}
              error={Boolean(i18nFieldError)}
              helperText={i18nFieldError}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <LocalizedTextField
              label={t('system.trackMaps.routeLabel')}
              value={watch('route.i18n')}
              onChange={value =>
                setValue('route.i18n', value, { shouldValidate: true, shouldDirty: true })
              }
              editorLang={editorLang}
              onEditorLangChange={setEditorLang}
              error={Boolean(routeI18nFieldError)}
              helperText={routeI18nFieldError}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <ControlledSelectField<FormTrackMap>
              control={control}
              fullWidth
              label={t('form.distance')}
              margin="dense"
              options={distanceOptions}
              withEmptyOption
              name="distance"
            />
          </Grid>

          {errors ? (
            <Grid size={{ xs: 12 }} sx={{ textAlign: 'right' }}>
              {Object.keys(errors).map(key => (
                <FormHelperText key={key} error>
                  {t('form.keysWithErrors', { key: t(`form.${key}`) })}
                </FormHelperText>
              ))}
            </Grid>
          ) : null}

          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={watch('published') ?? true}
                  onChange={e => setValue('published', e.target.checked)}
                />
              }
              label={t('system.sponsors.published')}
            />
          </Grid>
        </Grid>

        <EditorFormActions
          onCancel={handleClose}
          submitDisabled={isBusy}
          cancelDisabled={isBusy}
        />
      </form>
      <MediaManagerModal
        open={isMediaManagerOpen}
        onClose={() => setIsMediaManagerOpen(false)}
        onSelect={handleTrackMapSelect}
        mode="select"
        endpoint="trackMap"
        title={t('system.trackMaps.selectTrackMap')}
        showUploadButton={true}
      />
    </>
  );
};

export default TrackMapEditorForm;
